import React, { useEffect, useRef, useState } from "react";
import { api, formatMoney, formatApiError } from "../../lib/api";
import { maybeGzipFile } from "../../lib/upload";
import { uploadMasterChunked } from "../../lib/chunkUpload";
import { useContent } from "../../lib/content";
import { BRANDS, darkLogoFor } from "../../lib/brand";
import { Upload, Plus, Trash2, Download, Loader2, X, Wand2, Search, FileSpreadsheet, Database, Flame, Calendar, Percent, Layers, FileText, Edit2 } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 50;

export default function AdminProducts() {
  const { t } = useContent();
  const hufRate = Number(t("currency_huf_rate", "400")) || 400;
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [uploadingPromo, setUploadingPromo] = useState(false);
  const [lastPromoResult, setLastPromoResult] = useState(null);
  const [promoExpiry, setPromoExpiry] = useState("");
  const [promoStatus, setPromoStatus] = useState(null);
  const [uploadingMaster, setUploadingMaster] = useState(false);
  const [lastMasterResult, setLastMasterResult] = useState(null);
  const [masterProgress, setMasterProgress] = useState(null); // {processed, total, created, updated}
  const [masterBrand, setMasterBrand] = useState(""); // forced brand for this upload (audi/vw/skoda/seat/bmw/mercedes)
  // Mercedes-specific upload (3-column XLSX: Nr. Print, Benennung DE, Preis)
  const [uploadingMercedes, setUploadingMercedes] = useState(false); // false | "pkw" | "lkw"
  const [mercedesPriceCurrency, setMercedesPriceCurrency] = useState("EUR"); // "EUR" | "HUF"
  // BMW-specific upload (2-column XLSX: PARTNUMBER, price already containing 12% margin in EUR)
  const [uploadingBmw, setUploadingBmw] = useState(false);
  const [lastBmwResult, setLastBmwResult] = useState(null);
  const [bmwProgress, setBmwProgress] = useState(null);
  const [lastMercedesResult, setLastMercedesResult] = useState(null);
  const [mercedesProgress, setMercedesProgress] = useState(null);
  const [stockBrand, setStockBrand] = useState(""); // forced brand for ETS stock upload
  const [uploadingRg, setUploadingRg] = useState(false);
  const [rgCount, setRgCount] = useState(null);
  const [batches, setBatches] = useState([]);
  const [deletingBatchId, setDeletingBatchId] = useState(null);
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [cleanupStatus, setCleanupStatus] = useState(null); // {total_skus, processed_skus, deleted, status}
  const [bmwRebaseRunning, setBmwRebaseRunning] = useState(false);
  const [mercedesRebaseRunning, setMercedesRebaseRunning] = useState(false);
  const [splitPromoRunning, setSplitPromoRunning] = useState(false);
  const [promoBackfillRunning, setPromoBackfillRunning] = useState(false);
  const [editingPromoLabel, setEditingPromoLabel] = useState(null); // original label being edited
  const [editingPromoLabelText, setEditingPromoLabelText] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null); // product object being edited
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [brand, setBrand] = useState("");
  const [page, setPage] = useState(0);
  const fileRef = useRef();
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setQDebounced(q), 300);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => { setPage(0); }, [qDebounced, brand]);

  const fetchData = async () => {
    setLoading(true);
    const { data: p } = await api.get("/products", {
      params: { q: qDebounced || undefined, brand: brand || undefined, skip: page * PAGE_SIZE, limit: PAGE_SIZE },
    });
    setData(p);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDebounced, brand, page]);

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", await maybeGzipFile(file));
      const params = stockBrand ? { brand: stockBrand } : undefined;
      const { data: started } = await api.post("/admin/products/upload-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params,
        timeout: 0,
      });
      const jobId = started?.job_id;
      // Backwards-compatible: if the backend still returns the old sync
      // response (with `inserted`), surface it directly. New backend hands
      // us a job_id and we poll in the background.
      if (!jobId) {
        setLastResult(started);
        toast.success(`${started.inserted || 0} beszúrva · ${started.updated || 0} frissítve`);
        fetchData();
        fetchBatches();
        return;
      }
      // Resilient poll loop — tolerate transient 502/520 from Cloudflare
      // while a big bulk_write blocks the worker for a few seconds.
      let last;
      let consecErr = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const { data: job } = await api.get(`/admin/upload-jobs/${jobId}`, { timeout: 25000 });
          consecErr = 0;
          last = job;
          if (job.status === "done" || job.status === "error") break;
        } catch (e) {
          consecErr += 1;
          if (consecErr >= 60) {
            throw new Error("Nem sikerült lekérdezni a feltöltés állapotát. Frissítsd az oldalt és ellenőrizd az Aktív készletforrásokat.");
          }
        }
      }
      if (last?.status === "error") {
        toast.error(last.error || "ETS feldolgozási hiba");
      } else {
        const r = last?.result || {};
        setLastResult({
          inserted: r.inserted || last?.created || 0,
          updated: r.updated || last?.updated || 0,
          delimiter: r.delimiter || "?",
          layout: r.layout || "?",
          errors: [],
          error_count: 0,
        });
        toast.success(`ETS készlet: ${r.inserted || 0} új · ${r.updated || 0} frissítve`);
        fetchData();
        fetchBatches();
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    await uploadFile(file);
    if (e.target) e.target.value = "";
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    await uploadFile(file);
  };

  const fetchPromoStatus = async () => {
    try {
      const { data } = await api.get("/admin/promo-status");
      setPromoStatus(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchPromoStatus(); }, []);

  const uploadPromo = async (file) => {
    if (!file) return;
    setUploadingPromo(true);
    try {
      const fd = new FormData();
      fd.append("file", await maybeGzipFile(file));
      const params = {};
      if (promoExpiry) params.expires_at = promoExpiry;
      const { data } = await api.post("/admin/products/upload-promo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params,
      });
      setLastPromoResult(data);
      toast.success(`Akció: ${data.updated} frissítve · ${data.created} új cikkszám létrehozva`);
      fetchData();
      fetchPromoStatus();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingPromo(false);
    }
  };

  const handlePromoFile = async (e) => {
    const file = e.target.files?.[0];
    await uploadPromo(file);
    if (e.target) e.target.value = "";
  };

  const clearPromo = async (label) => {
    const msg = label
      ? `Biztosan lezárja az "${label}" kampányt? A termékek a normál árukra állnak vissza.`
      : "Biztosan minden aktív akciót lezár?";
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.post("/admin/products/clear-promo", null, {
        params: label ? { label } : {},
      });
      toast.success(`${data.deactivated} termék akciós állapota törölve`);
      fetchData();
      fetchPromoStatus();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const fetchRgCount = async () => {
    try {
      const { data } = await api.get("/admin/rabatt-groups");
      setRgCount(Array.isArray(data) ? data.length : 0);
    } catch { /* ignore */ }
  };

  const fetchBatches = async () => {
    try {
      const { data } = await api.get("/admin/upload-batches");
      setBatches(data?.batches || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchRgCount();
    fetchBatches();
  }, []);

  const deleteBatch = async (b) => {
    const msg = `Biztosan törli ezt a feltöltést?\n\n📄 ${b.filename}\n🗓 ${new Date(b.uploaded_at).toLocaleString("hu-HU")}\n📦 ${b.live_product_count.toLocaleString("hu-HU")} termék lesz törölve\n\n⚠ Nagy feltöltések (>10 000 termék) törlése akár 1–2 percig is eltarthat — a háttérben fut, ne zárd be az oldalt.\n\nEz a művelet nem visszavonható.`;
    if (!window.confirm(msg)) return;
    setDeletingBatchId(b.id);
    try {
      // Endpoint now returns 202-ish quickly (the actual delete runs in the
      // background). We poll /admin/upload-batches until this batch
      // disappears from the list (success) or its delete_status flips to
      // 'error'. Polling tolerates intermittent Cloudflare 520/504 just
      // like the upload-job poller does.
      await api.delete(`/admin/upload-batches/${b.id}`, { timeout: 60000 });
      toast.message("Törlés folyamatban...", { description: "Háttér-feldolgozás indult." });

      let consecutiveErrors = 0;
      const MAX_CONSECUTIVE_ERRORS = 60;
      let lastProgress = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, 2500));
        try {
          const { data } = await api.get("/admin/upload-batches", { timeout: 25000 });
          consecutiveErrors = 0;
          const list = data?.batches || [];
          const still = list.find((x) => x.id === b.id);
          if (!still) {
            toast.success(`${b.live_product_count.toLocaleString("hu-HU")} termék törölve`);
            break;
          }
          if (still.delete_status === "error") {
            toast.error(`Törlés sikertelen: ${still.delete_error || "ismeretlen hiba"}`);
            break;
          }
          const prog = still.delete_progress || 0;
          if (prog !== lastProgress) {
            lastProgress = prog;
            setBatches(list);
          }
        } catch (pollErr) {
          consecutiveErrors += 1;
          if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
            toast.warning("Nem érhető el a feltöltések listája — frissítsd az oldalt.");
            break;
          }
        }
      }
      fetchData();
      fetchBatches();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeletingBatchId(null);
    }
  };
  const renamePromoGroup = async (oldLabel) => {
    const next = (editingPromoLabelText || "").trim();
    if (!next) { toast.error("Az új név nem lehet üres"); return; }
    if (next === oldLabel) { setEditingPromoLabel(null); return; }
    try {
      const res = await api(`/api/admin/promo-groups/${encodeURIComponent(oldLabel)}`, {
        method: "PATCH",
        body: JSON.stringify({ new_label: next }),
      });
      toast.success(`Átnevezve: ${res.modified} termék kapott új akciónevet`);
      setEditingPromoLabel(null);
      setEditingPromoLabelText("");
      fetchPromoStatus && fetchPromoStatus();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const runSplitPromoOverlays = async () => {
    if (!window.confirm(
      "Régi „felülíró” akciók szétválasztása: a meglévő akciós termékeket átalakítja külön brand=„promo” sorokká, és visszaállítja az eredeti márkás (Audi/VW/BMW/...) termékek árát. " +
      "Az új design szerint az akciók NEM írják felül az eredeti cikket — külön sorként jelennek meg.\n\nFolytatod?"
    )) return;
    setSplitPromoRunning(true);
    try {
      const res = await api("/api/admin/products/split-promo-overlays", { method: "POST" });
      toast.success(
        `Szétválasztás kész: ${res.found_overlays.toLocaleString("hu-HU")} régi akciós sor megtalálva, ` +
        `${res.promo_records_written.toLocaleString("hu-HU")} promo-sor írva, ${res.originals_stripped.toLocaleString("hu-HU")} eredeti termék visszaállítva.`
      );
      fetchPromoStatus && fetchPromoStatus();
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSplitPromoRunning(false);
    }
  };

  const runPromoBackfill = async () => {
    if (!window.confirm(
      "Akciós cikkek márkájának pótlása: minden olyan akciós (promo) terméknél, ahol az „eredeti márka” mező üres, " +
      "megkeresi a hozzá tartozó normál (Audi/VW/Škoda/SEAT/BMW/Mercedes) változatot ugyanezzel a cikkszámmal, és átírja a márkáját. " +
      "Ezután a vevő márka-árrése helyesen rárakódik az akciós árra is.\n\nFolytatod?"
    )) return;
    setPromoBackfillRunning(true);
    try {
      const { data } = await api.post("/admin/products/backfill-promo-original-brand");
      const brands = Object.entries(data.brands_used || {}).map(([b, n]) => `${b}: ${n}`).join(" · ");
      toast.success(
        `Akciós márka pótolva: ${data.updated.toLocaleString("hu-HU")} / ${data.scanned.toLocaleString("hu-HU")} cikk` +
        `${data.still_unresolved > 0 ? ` (${data.still_unresolved} nem talált párt)` : ""}` +
        `${brands ? ` — ${brands}` : ""}`
      );
      fetchData();
      fetchPromoStatus && fetchPromoStatus();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPromoBackfillRunning(false);
    }
  };


  const runMercedesRebase = async () => {
    if (!window.confirm(
      "Mercedes árak átszorzása ×1,15-tel: minden meglévő Mercedes (Személy + Teher) terméket megszoroz 1,15-tel (15% árrés beégetése). " +
      "Csak egyszer fut le — a backend markerrel védi a dupla futás ellen.\n\nFolytatod?"
    )) return;
    setMercedesRebaseRunning(true);
    try {
      const res = await api("/api/admin/products/mercedes-rebase-15percent", { method: "POST" });
      if (res.status === "skipped") {
        toast.info(res.reason || "Már lefutott egyszer.");
      } else {
        toast.success(
          `Mercedes árazás átszorozva: ${res.updated.toLocaleString("hu-HU")} / ${res.scanned.toLocaleString("hu-HU")} termék frissítve (×${res.ratio}).`
        );
        fetchData();
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setMercedesRebaseRunning(false);
    }
  };

  const runBmwRebase = async () => {
    if (!window.confirm(
      "BMW árak átszámítása: a meglévő BMW termékek base_price értékét megszorozza ×1,15/1,12 ≈ 1,0268 (kb. +2,68%)-ra, hogy az új 15%-os képletre álljanak. " +
      "Csak egyszer fut le — a backend markerrel védi a dupla futás ellen.\n\nFolytatod?"
    )) return;
    setBmwRebaseRunning(true);
    try {
      const res = await api("/api/admin/products/bmw-rebase-15percent", { method: "POST" });
      if (res.status === "skipped") {
        toast.info(res.reason || "Már lefutott egyszer.");
      } else {
        toast.success(
          `BMW árazás átszámítva: ${res.updated.toLocaleString("hu-HU")} / ${res.scanned.toLocaleString("hu-HU")} termék frissítve (×${res.ratio}).`
        );
        fetchData();
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBmwRebaseRunning(false);
    }
  };

  const runOrphanCleanup = async () => {
    if (!window.confirm(
      "Duplikátum-takarítás: minden olyan terméket töröl, amelynek a márkája üres, " +
      "DE ugyanazzal a cikkszámmal létezik egy márkás (Audi/VW/BMW stb.) változat is. " +
      "A magányos „márka nélküli” termékek érintetlenek maradnak.\n\nFolytatod?"
    )) return;
    setCleanupRunning(true);
    setCleanupStatus({ status: "queued", total_skus: 0, processed_skus: 0, deleted: 0 });
    try {
      const start = await api("/api/admin/products/cleanup-orphans", { method: "POST" });
      const jobId = start?.job_id;
      if (!jobId) throw new Error("Backend nem adott vissza job_id-t");
      if (start.already_in_progress) {
        toast.info("Egy takarítás már fut a háttérben — figyeljük a meglévő futást.");
      }
      for (let i = 0; i < 600; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        try {
          const st = await api(`/api/admin/products/cleanup-orphans/${jobId}`);
          setCleanupStatus(st);
          if (st.status === "done") {
            toast.success(
              st.deleted > 0
                ? `Duplikátum-takarítás kész: ${st.deleted.toLocaleString("hu-HU")} márka nélküli duplikátum törölve.`
                : "Duplikátum-takarítás kész: nem találtunk duplikátumot."
            );
            fetchData();
            fetchBatches();
            break;
          }
          if (st.status === "error") {
            toast.error(`Takarítás sikertelen: ${st.error || "ismeretlen hiba"}`);
            break;
          }
        } catch (pollErr) {
          // transient — keep polling silently
        }
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCleanupRunning(false);
    }
  };



  const uploadMaster = async (file) => {
    if (!file) return;
    setUploadingMaster(true);
    setMasterProgress({ processed: 0, total: 0, created: 0, updated: 0, phase: "compress", upload_percent: 0 });
    setLastMasterResult(null);
    try {
      const started = await uploadMasterChunked(
        file,
        masterBrand || undefined,
        (p) => setMasterProgress((prev) => ({ ...(prev || {}), phase: p.phase, upload_percent: p.percent })),
      );
      const jobId = started?.job_id;
      if (!jobId) throw new Error("Backend nem adott vissza job_id-t");

      // Poll until done. Resilient to transient errors: the background job is
      // running on the server regardless of whether the polling HTTP request
      // fails (Cloudflare 520, MongoDB lock contention during bulk_write, brief
      // network blip). We swallow up to ~2 minutes of consecutive failures
      // before giving up — by then the job will almost certainly have
      // completed and the next successful poll will read its final state.
      let last;
      let consecutiveErrors = 0;
      const MAX_CONSECUTIVE_ERRORS = 60; // 60 × 2s ≈ 2 min tolerated outage
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const { data: job } = await api.get(`/admin/upload-jobs/${jobId}`, {
            timeout: 25000,
          });
          consecutiveErrors = 0;
          last = job;
          setMasterProgress({
            processed: job.processed_rows || 0,
            total: job.total_rows || 0,
            created: job.created || 0,
            updated: job.updated || 0,
            phase: "process",
            upload_percent: 100,
          });
          if (job.status === "done" || job.status === "error") break;
        } catch (pollErr) {
          consecutiveErrors += 1;
          if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
            throw new Error(
              "Nem sikerült lekérdezni a feltöltés állapotát több mint 2 percig. " +
              "A háttér-feldolgozás valószínűleg ettől függetlenül lefutott — frissítsd az oldalt és ellenőrizd az 'Aktív készlet-források' listát."
            );
          }
          // Surface the outage in the progress UI without killing the upload.
          setMasterProgress((prev) => ({
            ...(prev || { processed: 0, total: 0, created: 0, updated: 0, phase: "process", upload_percent: 100 }),
            phase: "process",
          }));
        }
      }
      if (last?.status === "error") {
        toast.error(last.error || "Feltöltési hiba");
      } else {
        setLastMasterResult(last.result || last);
        toast.success(`Mester lista: ${last.created || 0} létrehozva · ${last.updated || 0} frissítve`);
        fetchData();
        fetchBatches();
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingMaster(false);
      setMasterProgress(null);
    }
  };

  const handleMasterFile = async (e) => {
    const file = e.target.files?.[0];
    await uploadMaster(file);
    if (e.target) e.target.value = "";
  };

  // Mercedes upload — fixed brand="mercedes" (variant: pkw|lkw|combined), 3-column XLSX only
  const uploadMercedes = async (file, variant) => {
    if (!file) return;
    const lower = (file.name || "").toLowerCase();
    if (!lower.endsWith(".xlsx") && !lower.endsWith(".xlsm")) {
      toast.error("Csak XLSX (Excel) fájl támogatott a Mercedes árlistához.");
      return;
    }
    setUploadingMercedes(variant); // "pkw" | "lkw"
    setMercedesProgress({ processed: 0, total: 0, created: 0, updated: 0, phase: "compress", upload_percent: 0, variant });
    setLastMercedesResult(null);
    try {
      const started = await uploadMasterChunked(
        file,
        undefined,
        (p) => setMercedesProgress((prev) => ({ ...(prev || {}), phase: p.phase, upload_percent: p.percent })),
        { finalizeKind: "mercedes", brandIsFixed: true, variant, priceCurrency: mercedesPriceCurrency },
      );
      const jobId = started?.job_id;
      if (!jobId) throw new Error("Backend nem adott vissza job_id-t");

      let last;
      let consecutiveErrors = 0;
      const MAX_CONSECUTIVE_ERRORS = 60;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const { data: job } = await api.get(`/admin/upload-jobs/${jobId}`, { timeout: 25000 });
          consecutiveErrors = 0;
          last = job;
          setMercedesProgress({
            processed: job.processed_rows || 0,
            total: job.total_rows || 0,
            created: job.created || 0,
            updated: job.updated || 0,
            phase: "process",
            upload_percent: 100,
            variant,
          });
          if (job.status === "done" || job.status === "error") break;
        } catch (pollErr) {
          consecutiveErrors += 1;
          if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
            throw new Error(
              "Nem sikerült lekérdezni a feltöltés állapotát több mint 2 percig. " +
              "A háttér-feldolgozás valószínűleg ettől függetlenül lefutott — frissítsd az oldalt."
            );
          }
          setMercedesProgress((prev) => ({
            ...(prev || { processed: 0, total: 0, created: 0, updated: 0, phase: "process", upload_percent: 100, variant }),
            phase: "process",
            variant,
          }));
        }
      }
      if (last?.status === "error") {
        toast.error(last.error || "Mercedes feltöltési hiba");
      } else {
        setLastMercedesResult({ ...(last.result || last), variant });
        const variantLabel = variant === "lkw" ? "Mercedes Teher (LKW)" : "Mercedes Személy (PKW)";
        toast.success(`${variantLabel}: ${last.created || 0} létrehozva · ${last.updated || 0} frissítve`);
        fetchData();
        fetchBatches();
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingMercedes(false);
      setMercedesProgress(null);
    }
  };

  const handleMercedesPkwFile = async (e) => {
    const file = e.target.files?.[0];
    await uploadMercedes(file, "pkw");
    if (e.target) e.target.value = "";
  };
  const handleMercedesLkwFile = async (e) => {
    const file = e.target.files?.[0];
    await uploadMercedes(file, "lkw");
    if (e.target) e.target.value = "";
  };

  // BMW upload — fixed brand="bmw", 2-column XLSX (PARTNUMBER + price w/ 12% pre-baked)
  const uploadBmw = async (file) => {
    if (!file) return;
    const lower = (file.name || "").toLowerCase();
    if (!lower.endsWith(".xlsx") && !lower.endsWith(".xlsm")) {
      toast.error("Csak XLSX (Excel) fájl támogatott a BMW árlistához.");
      return;
    }
    setUploadingBmw(true);
    setBmwProgress({ processed: 0, total: 0, created: 0, updated: 0, phase: "compress", upload_percent: 0 });
    setLastBmwResult(null);
    try {
      const started = await uploadMasterChunked(
        file,
        undefined,
        (p) => setBmwProgress((prev) => ({ ...(prev || {}), phase: p.phase, upload_percent: p.percent })),
        { finalizeKind: "bmw", brandIsFixed: true },
      );
      const jobId = started?.job_id;
      if (!jobId) throw new Error("Backend nem adott vissza job_id-t");

      let last;
      let consecutiveErrors = 0;
      const MAX_CONSECUTIVE_ERRORS = 60;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const { data: job } = await api.get(`/admin/upload-jobs/${jobId}`, { timeout: 25000 });
          consecutiveErrors = 0;
          last = job;
          setBmwProgress({
            processed: job.processed_rows || 0,
            total: job.total_rows || 0,
            created: job.created || 0,
            updated: job.updated || 0,
            phase: "process",
            upload_percent: 100,
          });
          if (job.status === "done" || job.status === "error") break;
        } catch (pollErr) {
          consecutiveErrors += 1;
          if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
            throw new Error(
              "Nem sikerült lekérdezni a feltöltés állapotát több mint 2 percig. " +
              "A háttér-feldolgozás valószínűleg ettől függetlenül lefutott — frissítsd az oldalt."
            );
          }
        }
      }
      if (last?.status === "error") {
        toast.error(last.error || "BMW feltöltési hiba");
      } else {
        setLastBmwResult(last.result || last);
        toast.success(`BMW lista: ${last.created || 0} létrehozva · ${last.updated || 0} frissítve`);
        fetchData();
        fetchBatches();
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingBmw(false);
      setBmwProgress(null);
    }
  };

  const handleBmwFile = async (e) => {
    const file = e.target.files?.[0];
    await uploadBmw(file);
    if (e.target) e.target.value = "";
  };

  const uploadRg = async (file) => {
    if (!file) return;
    // Client-side guard: drag-and-drop bypasses the input's `accept` filter,
    // so a .txt master list dropped here would hit the backend and either
    // 400 or — for huge files — trip Cloudflare 520. Reject early.
    const lower = (file.name || "").toLowerCase();
    if (!/\.(xlsx|xlsm)$/.test(lower)) {
      toast.error(
        `Ez a felület csak Excel (.xlsx) Rabattgruppen táblát fogad. ` +
        `A '${file.name}' helyett használd a 'Mester készlet/árlista' kártyát .txt/.csv fájlokhoz.`
      );
      return;
    }
    setUploadingRg(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/rabatt-groups/upload-xlsx", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setRgCount(data.count);
      toast.success(`Rabattgruppen tábla mentve: ${data.count} bejegyzés`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingRg(false);
    }
  };

  const handleRgFile = async (e) => {
    const file = e.target.files?.[0];
    await uploadRg(file);
    if (e.target) e.target.value = "";
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Biztosan törli az alkatrészt?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Törölve");
    fetchData();
  };

  const downloadTemplate = () => {
    const headers = ["sku", "name", "description", "category", "stock", "base_price", "image_url"];
    const sample = ["SKU-001", "Fékbetét készlet", "Első tengely, OEM", "Fék", "150", "24.99", ""];
    const csv = headers.join(",") + "\n" + sample.join(",") + "\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "vag_alkatresz_sablon.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-2">
        <div>
          <h1 className="text-2xl font-bold uppercase tracking-wide">Készlet</h1>
          <p className="text-sm" style={{ color: "var(--text-mute)" }}>
            <strong style={{ color: "var(--brand)" }}>{data.total.toLocaleString("hu-HU")}</strong> cikkszám az adatbázisban
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={downloadTemplate} className="btn-ghost" data-testid="download-template-btn">
            <Download size={12} /> Sablon
          </button>
          <button
            onClick={async () => {
              if (!window.confirm("Az összes alkatrész márkáját automatikusan besorolja a cikkszám prefix alapján. Folytatja?")) return;
              try {
                const { data } = await api.post("/admin/products/auto-brand");
                const dist = Object.entries(data.by_brand || {}).map(([k, v]) => `${k}:${v}`).join(" · ");
                toast.success(`Besorolva: ${data.updated.toLocaleString("hu-HU")} (${dist})`);
                fetchData();
              } catch (e) { toast.error(formatApiError(e)); }
            }}
            className="btn-ghost"
            data-testid="auto-brand-btn"
          >
            <Wand2 size={12} /> Márka besorolás
          </button>
          <button
            onClick={async () => {
              const ok = window.confirm(
                "💣 VESZÉLY: Ez TÖRÖL minden terméket, készletet és feltöltési naplót.\n\n" +
                "A felhasználói fiókok, rendelések és Rabattgruppen tábla MEGMARADNAK.\n\n" +
                "Biztosan folytatod?"
              );
              if (!ok) return;
              try {
                const { data } = await api.delete("/admin/products/clear-all");
                toast.success(
                  `Törölve: ${data.products_deleted?.toLocaleString("hu-HU")} termék · ` +
                  `${data.batches_deleted} batch · ${data.jobs_deleted} job`
                );
                fetchData();
                fetchBatches();
              } catch (e) { toast.error(formatApiError(e)); }
            }}
            className="btn-ghost"
            style={{ color: "#dc2626", borderColor: "#dc2626" }}
            data-testid="clear-all-btn"
          >
            <Trash2 size={12} /> Mindent töröl
          </button>
          <button onClick={() => setShowCreate((s) => !s)} className="btn-yellow" data-testid="toggle-create-product">
            <Plus size={12} /> Új alkatrész
          </button>
        </div>
      </div>
      <div className="border-b-2 mb-5" style={{ borderColor: "var(--brand)" }} />

      {/* ACTIVE STOCK SOURCES — list of uploaded master/ETS batches */}
      <div className="card-elev mb-5 overflow-hidden" data-testid="batches-card">
        <div className="section-title flex items-center gap-2 justify-between" style={{ background: "linear-gradient(180deg, #334155 0%, #0f172a 100%)" }}>
          <span className="flex items-center gap-2">
            <Layers size={14} /> Aktív készlet-források ({batches.length})
          </span>
          <button
            onClick={runOrphanCleanup}
            disabled={cleanupRunning}
            className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded inline-flex items-center gap-1.5 transition-colors disabled:opacity-60"
            style={{ background: "#c8102e", color: "#fff" }}
            data-testid="cleanup-orphans-btn"
            title="Törli a márka nélküli duplikátum cikkszámokat, ahol létezik márkás változat is."
          >
            {cleanupRunning ? (
              <>
                <Loader2 size={12} className="animate-spin" />
                {cleanupStatus?.status === "queued"
                  ? "Beolvasás..."
                  : `Takarítás... ${(cleanupStatus?.deleted || 0).toLocaleString("hu-HU")} törölve`}
              </>
            ) : (
              <>
                <Trash2 size={12} /> Duplikátumok törlése
              </>
            )}
          </button>
        </div>
        <div className="p-5">
          {batches.length === 0 ? (
            <div className="text-sm text-center py-8" style={{ color: "var(--text-mute)" }}>
              <Layers size={32} className="mx-auto mb-3 opacity-40" />
              Még nincs aktív készlet-forrás. Töltsd fel az első mester vagy ETS készlet listát az alábbi kártyák egyikével.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="batches-list">
              {batches.map((b) => {
                const KIND_LABEL = {
                  master: "Mester készlet",
                  "ets-stock": "ETS készlet",
                  mercedes: "Mercedes árlista",
                  bmw: "BMW árlista",
                };
                const KIND_COLOR = {
                  master: "#0f766e",
                  "ets-stock": "#2563eb",
                  mercedes: "#1f2937",
                  bmw: "#0066B1",
                };
                const kindLabel = KIND_LABEL[b.kind] || b.kind;
                const kindColor = KIND_COLOR[b.kind] || "#475569";
                const BRAND_LABEL = {
                  "mercedes-pkw": "Mercedes PKW",
                  "mercedes-lkw": "Mercedes LKW",
                  bmw: "BMW",
                  vw: "VW",
                };
                const brandLabel = b.brand
                  ? BRAND_LABEL[b.brand] || (b.brand.charAt(0).toUpperCase() + b.brand.slice(1))
                  : null;
                return (
                  <li
                    key={b.id}
                    className="flex items-center gap-4 p-3 rounded border bg-white"
                    style={{ borderColor: "var(--border)" }}
                    data-testid={`batch-row-${b.id}`}
                  >
                    <div
                      className="h-9 w-9 rounded grid place-items-center flex-shrink-0 text-white"
                      style={{ background: kindColor }}
                    >
                      <FileText size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-sm truncate" title={b.filename}>
                          {b.filename}
                        </span>
                        <span
                          className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                          style={{ background: kindColor, color: "#fff" }}
                        >
                          {kindLabel}
                        </span>
                        {brandLabel && (
                          <span
                            className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                            style={{ background: "var(--surface-elev)", color: "var(--text)", border: "1px solid var(--border)" }}
                            data-testid={`batch-brand-${b.id}`}
                          >
                            {BRANDS[b.brand] && (
                              <img src={darkLogoFor(BRANDS[b.brand])} alt="" className="h-3 w-3 object-contain" />
                            )}
                            {brandLabel}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] mt-0.5 flex items-center gap-3 flex-wrap" style={{ color: "var(--text-mute)" }}>
                        <span>📦 <strong>{b.live_product_count.toLocaleString("hu-HU")}</strong> aktív termék</span>
                        {typeof b.created === "number" && (
                          <span>✚ {b.created.toLocaleString("hu-HU")} létrehozva · ⟳ {b.updated.toLocaleString("hu-HU")} frissítve</span>
                        )}
                        <span>🕒 {new Date(b.uploaded_at).toLocaleString("hu-HU")}</span>
                        {b.delete_status === "deleting" && (
                          <span style={{ color: "#c8102e", fontWeight: 700 }} data-testid={`batch-delete-progress-${b.id}`}>
                            🗑 Törlés... {(b.delete_progress || 0).toLocaleString("hu-HU")} eddig törölve
                          </span>
                        )}
                        {b.delete_status === "error" && (
                          <span style={{ color: "#c8102e", fontWeight: 700 }} data-testid={`batch-delete-error-${b.id}`}>
                            ⚠ Törlés sikertelen: {b.delete_error || "ismeretlen hiba"}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => deleteBatch(b)}
                      disabled={deletingBatchId === b.id}
                      className="btn-danger !py-1.5 !px-3 text-xs flex-shrink-0"
                      data-testid={`delete-batch-${b.id}`}
                      title="Törli a feltöltést és az összes hozzá tartozó terméket"
                    >
                      {deletingBatchId === b.id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <>
                          <Trash2 size={14} /> Törlés
                        </>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>


      {/* RABATTGRUPPEN — RG → % engedmény tábla */}
      <div className="card-elev mb-5 overflow-hidden" data-testid="rg-upload-card">
        <div className="section-title flex items-center gap-2" style={{ background: "linear-gradient(180deg, #6b46c1 0%, #4c1d95 100%)" }}>
          <Percent size={14} /> Rabattgruppen (RG → % engedmény) tábla
        </div>
        <div className="p-5 grid lg:grid-cols-[1fr_320px] gap-5 items-stretch">
          <div className="text-sm" style={{ color: "var(--text)" }}>
            <ul className="space-y-1.5">
              <li>• A Mester készlet feltöltés ezt a táblát használja az árak kiszámításához</li>
              <li>• <strong>Excel (XLSX)</strong> formátum — első oszlop: Rabattgruppe szám, második oszlop: Rabatt %</li>
              <li>• Több sheet is támogatott — az első előfordulás nyer</li>
              <li>• Jelenleg <strong style={{ color: rgCount > 0 ? "var(--in-stock)" : "var(--out-stock)" }}>{rgCount ?? "..."}</strong> bejegyzés van a táblában</li>
            </ul>
          </div>
          <label
            className="block cursor-pointer text-center p-4 transition-all relative bg-white flex flex-col items-center justify-center"
            style={{
              border: `2px dashed ${uploadingRg ? "var(--brand)" : "#6b46c1"}`,
              borderRadius: 4,
              background: uploadingRg ? "#fff" : "rgba(107,70,193,0.05)",
            }}
            data-testid="rg-upload-dropzone"
          >
            {uploadingRg ? (
              <Loader2 className="mx-auto animate-spin mb-2" size={28} style={{ color: "#6b46c1" }} />
            ) : (
              <Percent className="mx-auto mb-2" size={28} style={{ color: "#6b46c1" }} />
            )}
            <div className="font-bold text-sm uppercase" style={{ color: "#6b46c1" }}>
              {uploadingRg ? "Feldolgozás..." : "Húzza ide az XLSX-et"}
            </div>
            <div className="text-[11px] mt-1" style={{ color: "var(--text-mute)" }}>rabattgruppen_export.xlsx</div>
            <input
              type="file"
              accept=".xlsx,.xlsm"
              className="hidden"
              onChange={handleRgFile}
              disabled={uploadingRg}
              data-testid="rg-upload-input"
            />
          </label>
        </div>
      </div>

      {/* MASTER pricelist upload — Teilenummer;Bezeichnung;Preis;RG */}
      <div className="card-elev mb-5 overflow-hidden" data-testid="master-upload-card">
        <div className="section-title flex items-center gap-2" style={{ background: "linear-gradient(180deg, #0f766e 0%, #134e4a 100%)" }}>
          <Database size={14} /> Mester készlet/árlista (Teilenummer;Bezeichnung;Preis;RG) — automatikus RG-engedmény
        </div>
        <div className="p-5 grid lg:grid-cols-[1fr_320px] gap-5 items-stretch">
          <div className="text-sm" style={{ color: "var(--text)" }}>
            <ul className="space-y-1.5">
              <li>• <strong>Új formátum:</strong> <code className="font-mono">Teilenummer;Bezeichnung;Preis;RG</code></li>
              <li>• Példa: <code className="font-mono">000000999;SELBSTBAU;57732,97;29</code></li>
              <li>• <code className="font-mono">.txt</code>, <code className="font-mono">.csv</code>, <code className="font-mono">.tsv</code> és XLSX is támogatott</li>
              <li>• Vessző vagy pont decimális elválasztó automatikusan kezelve</li>
              <li>• <strong>Árazás:</strong> base_price = Preis × (1 − RG % / 100). Erre rakódik a vevő márka-árrése.</li>
              <li>• Új cikkszámok automatikusan létrejönnek (<strong>készlet 0, beszerzés 2–3 nap</strong>)</li>
              <li>• Meglévő cikkszámok készletét NEM módosítja</li>
            </ul>
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-xs font-bold uppercase">
              Márka (kötelező többfájlos feltöltéshez)
            </label>
            <select
              value={masterBrand}
              onChange={(e) => setMasterBrand(e.target.value)}
              className="input !py-1.5"
              data-testid="master-brand-select"
            >
              <option value="">— Cikkszám előtag alapján —</option>
              <option value="audi">Audi</option>
              <option value="vw">Volkswagen</option>
              <option value="skoda">Škoda</option>
              <option value="seat">Seat</option>
              <option value="bmw">BMW</option>
              <option value="mercedes">Mercedes</option>
            </select>
            <div className="text-[10px]" style={{ color: "var(--text-mute)" }}>
              Ha kiválasztod: minden cikk ezt a márkát kapja, és ugyanaz az SKU más márkás listán külön sorként él tovább.
            </div>
            <label
              className="block cursor-pointer text-center p-4 transition-all relative bg-white flex-1 flex flex-col items-center justify-center"
              style={{
                border: `2px dashed ${uploadingMaster ? "var(--brand)" : "#0f766e"}`,
                borderRadius: 4,
                background: uploadingMaster ? "#fff" : "rgba(15,118,110,0.05)",
              }}
              data-testid="master-upload-dropzone"
            >
              {uploadingMaster ? (
                <Loader2 className="mx-auto animate-spin mb-2" size={28} style={{ color: "#0f766e" }} />
              ) : (
                <Database className="mx-auto mb-2" size={28} style={{ color: "#0f766e" }} />
              )}
              <div className="font-bold text-sm uppercase" style={{ color: "#0f766e" }}>
                {uploadingMaster ? "Feldolgozás..." : "Húzza ide a mester listát"}
              </div>
              <div className="text-[11px] mt-1" style={{ color: "var(--text-mute)" }}>.txt / .csv / .xlsx</div>
              <input
                type="file"
                accept=".csv,.tsv,.txt,.xlsx,.xlsm"
                className="hidden"
                onChange={handleMasterFile}
                disabled={uploadingMaster}
                data-testid="master-upload-input"
              />
            </label>
          </div>
        </div>

        {lastMasterResult && !uploadingMaster && (
          <div className="px-5 pb-5 grid grid-cols-3 gap-3 text-sm" data-testid="master-result">
            <ResultStat label="Sor feldolgozva" value={lastMasterResult.rows} color="var(--brand)" />
            <ResultStat label="Új cikkszám" value={lastMasterResult.created} color="var(--in-stock)" />
            <ResultStat label="Frissítve" value={lastMasterResult.updated} color="#0f766e" />
            {lastMasterResult.rg_misses && lastMasterResult.rg_misses.length > 0 && (
              <div className="col-span-3 text-[11px] p-2 rounded" style={{ background: "#fff5f5", border: "1px solid var(--out-stock)", color: "var(--out-stock)" }}>
                ⚠ Ismeretlen RG-k a fájlban: {lastMasterResult.rg_misses.map(m => `RG${m.rg} (${m.count} db)`).join(", ")}. Ezeknél a listaárat használtuk engedmény nélkül — egészítsd ki a Rabattgruppen táblát!
              </div>
            )}
          </div>
        )}
        {uploadingMaster && masterProgress && (
          <div className="px-5 pb-5" data-testid="master-progress">
            <div className="flex items-center justify-between text-xs font-bold uppercase mb-2" style={{ color: "#0f766e" }}>
              <span>
                {masterProgress.phase === "compress" && "Tömörítés a böngészőben..."}
                {masterProgress.phase === "upload" && "Feltöltés folyamatban (chunked)..."}
                {masterProgress.phase === "finalize" && "Fájl összeállítása a szerveren..."}
                {(masterProgress.phase === "process" || !masterProgress.phase) && "Háttér-feldolgozás folyamatban — ne zárd be az oldalt"}
              </span>
              <span>
                {masterProgress.phase === "process" || !masterProgress.phase
                  ? (masterProgress.total > 0
                      ? `${Math.round((masterProgress.processed / Math.max(masterProgress.total, 1)) * 100)}%`
                      : "Fájl olvasása...")
                  : `${masterProgress.upload_percent || 0}%`}
              </span>
            </div>
            <div className="w-full h-3 rounded overflow-hidden" style={{ background: "rgba(15,118,110,0.12)" }}>
              <div
                className="h-full transition-all duration-300"
                style={{
                  width:
                    masterProgress.phase === "process" || !masterProgress.phase
                      ? `${masterProgress.total > 0 ? Math.min(100, (masterProgress.processed / masterProgress.total) * 100) : 5}%`
                      : `${Math.max(2, masterProgress.upload_percent || 0)}%`,
                  background: "linear-gradient(90deg, #0f766e 0%, #14b8a6 100%)",
                }}
                data-testid="master-progress-bar"
              />
            </div>
            <div className="mt-2 grid grid-cols-3 gap-3 text-xs" style={{ color: "var(--text-mute)" }}>
              <span><strong>{masterProgress.processed.toLocaleString("hu-HU")}</strong>{masterProgress.total > 0 && ` / ${masterProgress.total.toLocaleString("hu-HU")}`} sor</span>
              <span>Új: <strong style={{ color: "var(--in-stock)" }}>{masterProgress.created.toLocaleString("hu-HU")}</strong></span>
              <span>Frissítve: <strong style={{ color: "#0f766e" }}>{masterProgress.updated.toLocaleString("hu-HU")}</strong></span>
            </div>
          </div>
        )}
      </div>

      {/* MERCEDES pricelist upload — 3-column XLSX only, two variants:
          PKW (passenger) and LKW (truck). Each lands under its own sub-brand
          so the same SKU can legitimately exist in both lists without
          overwriting each other. */}
      <div className="card-elev mb-5 overflow-hidden" data-testid="mercedes-upload-card">
        <div className="section-title flex items-center gap-2" style={{ background: "linear-gradient(180deg, #1f2937 0%, #111827 100%)" }}>
          <Database size={14} /> Mercedes árlista (Személy / Teher · 15% árrés automatikus) — külön Mercedes csoport
        </div>
        <div className="p-5 grid lg:grid-cols-[1fr_320px] gap-5 items-stretch">
          <div className="text-sm" style={{ color: "var(--text)" }}>
            <ul className="space-y-1.5">
              <li>• <strong>Formátum:</strong> 3 oszlopos XLSX (Excel) — <code className="font-mono">Nr. Print</code>, <code className="font-mono">Benennung DE</code>, <code className="font-mono">Preis</code></li>
              <li>• Cikkszám belső szóközei automatikusan figyelmen kívül (<code className="font-mono">A   000 000 00 00</code> → <code className="font-mono">A0000000000</code>)</li>
              <li>• <strong>Személy</strong> = <code className="font-mono">mercedes-pkw</code>; <strong>Teher</strong> = <code className="font-mono">mercedes-lkw</code> — két külön sub-brand, így ütköző cikkszámok nem írják felül egymást</li>
              <li>• <strong>Új árazás (a rendszer csinálja):</strong> <code className="font-mono">base_price = Preis × 1,15</code> (HUF-os feltöltésnél előtte <code className="font-mono">/ {hufRate}</code> árfolyam)</li>
              <li>• <strong>Műhely-szintű árrés Mercedesre nem érvényes</strong> — minden vevő ugyanazt az árat látja</li>
              <li>• Új cikkszámok automatikusan létrejönnek (<strong>készlet 0, beszerzés 2–3 nap</strong>)</li>
              <li>• Akár ~1M soros XLSX is támogatott — chunked feltöltéssel, háttér-feldolgozással</li>
            </ul>
            <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--border)" }}>
              <div className="text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: "var(--text-mute)" }}>
                Az Excel „Preis" oszlopa milyen pénznemben van?
              </div>
              <div className="flex gap-2" data-testid="mercedes-currency-toggle">
                {["EUR", "HUF"].map((cur) => (
                  <label
                    key={cur}
                    className="flex items-center gap-1.5 cursor-pointer px-3 py-1.5 rounded border text-xs font-bold"
                    style={{
                      borderColor: mercedesPriceCurrency === cur ? "#1f2937" : "var(--border)",
                      background: mercedesPriceCurrency === cur ? "#1f2937" : "var(--surface-elev)",
                      color: mercedesPriceCurrency === cur ? "#fff" : "var(--text)",
                    }}
                  >
                    <input
                      type="radio"
                      name="mercedes-currency"
                      value={cur}
                      checked={mercedesPriceCurrency === cur}
                      onChange={() => setMercedesPriceCurrency(cur)}
                      disabled={!!uploadingMercedes}
                      className="sr-only"
                      data-testid={`mercedes-currency-${cur.toLowerCase()}`}
                    />
                    {cur}
                  </label>
                ))}
              </div>
              <div className="text-[11px] mt-1.5" style={{ color: "var(--text-mute)" }}>
                HUF választása esetén a rendszer leosztja a {hufRate} Ft/€ árfolyammal, mielőtt rárakja a 15% árrést.
              </div>
            </div>
            <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--border)" }}>
              <div className="text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: "var(--text-mute)" }}>
                Régi Mercedes árak átszorzása ×1,15-tel
              </div>
              <div className="text-xs mb-2" style={{ color: "var(--text-mute)" }}>
                Egyszeri művelet: minden meglévő Mercedes (Személy + Teher) terméket megszoroz ×1,15-tel (15% árrés beégetése). Markerrel védve dupla futás ellen.
              </div>
              <button
                onClick={runMercedesRebase}
                disabled={mercedesRebaseRunning}
                className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded inline-flex items-center gap-1.5 transition-colors disabled:opacity-60"
                style={{ background: "#1f2937", color: "#fff" }}
                data-testid="mercedes-rebase-btn"
                title="A meglévő Mercedes árakat megszorozza ×1,15-tel, hogy a 15% árrés be legyen égetve"
              >
                {mercedesRebaseRunning ? (
                  <><Loader2 size={12} className="animate-spin" /> Átszámítás...</>
                ) : (
                  <>Mercedes árak ×1,15</>
                )}
              </button>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {/* PKW (Passenger) */}
            <label
              className="block cursor-pointer text-center p-3 transition-all relative bg-white flex flex-col items-center justify-center"
              style={{
                border: `2px dashed ${uploadingMercedes === "pkw" ? "var(--brand)" : "#1f2937"}`,
                borderRadius: 4,
                background: uploadingMercedes === "pkw" ? "#fff" : "rgba(31,41,55,0.05)",
                opacity: uploadingMercedes && uploadingMercedes !== "pkw" ? 0.4 : 1,
              }}
              data-testid="mercedes-pkw-upload-dropzone"
            >
              {uploadingMercedes === "pkw" ? (
                <Loader2 className="mx-auto animate-spin mb-1" size={22} style={{ color: "#1f2937" }} />
              ) : (
                <Database className="mx-auto mb-1" size={22} style={{ color: "#1f2937" }} />
              )}
              <div className="font-bold text-xs uppercase" style={{ color: "#1f2937" }}>
                {uploadingMercedes === "pkw" ? "Feldolgozás..." : "Mercedes SZEMÉLY (PKW)"}
              </div>
              <div className="text-[10px] mt-0.5" style={{ color: "var(--text-mute)" }}>.xlsx — sub-brand: mercedes-pkw</div>
              <input
                type="file"
                accept=".xlsx,.xlsm"
                className="hidden"
                onChange={handleMercedesPkwFile}
                disabled={!!uploadingMercedes}
                data-testid="mercedes-pkw-upload-input"
              />
            </label>
            {/* LKW (Truck) */}
            <label
              className="block cursor-pointer text-center p-3 transition-all relative bg-white flex flex-col items-center justify-center"
              style={{
                border: `2px dashed ${uploadingMercedes === "lkw" ? "var(--brand)" : "#1f2937"}`,
                borderRadius: 4,
                background: uploadingMercedes === "lkw" ? "#fff" : "rgba(31,41,55,0.05)",
                opacity: uploadingMercedes && uploadingMercedes !== "lkw" ? 0.4 : 1,
              }}
              data-testid="mercedes-lkw-upload-dropzone"
            >
              {uploadingMercedes === "lkw" ? (
                <Loader2 className="mx-auto animate-spin mb-1" size={22} style={{ color: "#1f2937" }} />
              ) : (
                <Database className="mx-auto mb-1" size={22} style={{ color: "#1f2937" }} />
              )}
              <div className="font-bold text-xs uppercase" style={{ color: "#1f2937" }}>
                {uploadingMercedes === "lkw" ? "Feldolgozás..." : "Mercedes TEHER (LKW)"}
              </div>
              <div className="text-[10px] mt-0.5" style={{ color: "var(--text-mute)" }}>.xlsx — sub-brand: mercedes-lkw</div>
              <input
                type="file"
                accept=".xlsx,.xlsm"
                className="hidden"
                onChange={handleMercedesLkwFile}
                disabled={!!uploadingMercedes}
                data-testid="mercedes-lkw-upload-input"
              />
            </label>
          </div>
        </div>

        {lastMercedesResult && !uploadingMercedes && (
          <div className="px-5 pb-5 grid grid-cols-3 gap-3 text-sm" data-testid="mercedes-result">
            <ResultStat
              label={`Sor feldolgozva (${lastMercedesResult.variant === "lkw" ? "Teher" : "Személy"})`}
              value={lastMercedesResult.rows} color="var(--brand)" />
            <ResultStat label="Új cikkszám" value={lastMercedesResult.created} color="var(--in-stock)" />
            <ResultStat label="Frissítve" value={lastMercedesResult.updated} color="#1f2937" />
            {lastMercedesResult.skipped_no_price > 0 && (
              <div className="col-span-3 text-[11px] p-2 rounded" style={{ background: "#fff5f5", border: "1px solid var(--out-stock)", color: "var(--out-stock)" }}>
                ⚠ {lastMercedesResult.skipped_no_price.toLocaleString("hu-HU")} sort kihagytunk hiányzó vagy 0 ár miatt.
              </div>
            )}
          </div>
        )}
        {uploadingMercedes && mercedesProgress && (
          <div className="px-5 pb-5" data-testid="mercedes-progress">
            <div className="flex items-center justify-between text-xs font-bold uppercase mb-2" style={{ color: "#1f2937" }}>
              <span>
                {mercedesProgress.variant === "lkw" ? "TEHER · " : "SZEMÉLY · "}
                {mercedesProgress.phase === "compress" && "Tömörítés a böngészőben..."}
                {mercedesProgress.phase === "upload" && "Feltöltés folyamatban (chunked)..."}
                {mercedesProgress.phase === "finalize" && "Fájl összeállítása a szerveren..."}
                {(mercedesProgress.phase === "process" || !mercedesProgress.phase) && "Háttér-feldolgozás folyamatban — ne zárd be az oldalt"}
              </span>
              <span>
                {mercedesProgress.phase === "process" || !mercedesProgress.phase
                  ? (mercedesProgress.total > 0
                      ? `${Math.round((mercedesProgress.processed / Math.max(mercedesProgress.total, 1)) * 100)}%`
                      : "Fájl olvasása...")
                  : `${mercedesProgress.upload_percent || 0}%`}
              </span>
            </div>
            <div className="w-full h-3 rounded overflow-hidden" style={{ background: "rgba(31,41,55,0.12)" }}>
              <div
                className="h-full transition-all duration-300"
                style={{
                  width:
                    mercedesProgress.phase === "process" || !mercedesProgress.phase
                      ? `${mercedesProgress.total > 0 ? Math.min(100, (mercedesProgress.processed / mercedesProgress.total) * 100) : 5}%`
                      : `${Math.max(2, mercedesProgress.upload_percent || 0)}%`,
                  background: "linear-gradient(90deg, #1f2937 0%, #6b7280 100%)",
                }}
                data-testid="mercedes-progress-bar"
              />
            </div>
            <div className="mt-2 grid grid-cols-3 gap-3 text-xs" style={{ color: "var(--text-mute)" }}>
              <span><strong>{mercedesProgress.processed.toLocaleString("hu-HU")}</strong>{mercedesProgress.total > 0 && ` / ${mercedesProgress.total.toLocaleString("hu-HU")}`} sor</span>
              <span>Új: <strong style={{ color: "var(--in-stock)" }}>{mercedesProgress.created.toLocaleString("hu-HU")}</strong></span>
              <span>Frissítve: <strong style={{ color: "#1f2937" }}>{mercedesProgress.updated.toLocaleString("hu-HU")}</strong></span>
            </div>
          </div>
        )}
      </div>

      {/* BMW pricelist upload — 2-column XLSX (PARTNUMBER + price in HUF).
          The worker bakes a 15% markup into the HUF price and converts to
          EUR using the admin currency_huf_rate before storing as base_price. */}
      <div className="card-elev mb-5 overflow-hidden" data-testid="bmw-upload-card">
        <div className="section-title flex items-center gap-2" style={{ background: "linear-gradient(180deg, #0066B1 0%, #004C8C 100%)" }}>
          <Database size={14} /> BMW árlista (PARTNUMBER; Preis EUR · 15% árrés automatikus) — külön BMW csoport
        </div>
        <div className="p-5 grid lg:grid-cols-[1fr_320px] gap-5 items-stretch">
          <div className="text-sm" style={{ color: "var(--text)" }}>
            <ul className="space-y-1.5">
              <li>• <strong>Formátum:</strong> 2 oszlopos XLSX (Excel) — <code className="font-mono">cikkszám</code>, <code className="font-mono">ár (EUR)</code></li>
              <li>• <strong>Új árazás (a rendszer csinálja):</strong> <code className="font-mono">base_price (EUR) = EUR_ár × 1,15</code> — automatikus 15% árrés</li>
              <li>• <strong>Nincs Excelben számolnivaló</strong> — a beszállítói EUR nettó kerül a B oszlopba, semmi több</li>
              <li>• A webshop oldalon a vevő EUR-ban látja; HUF nézetben <code className="font-mono">base × {hufRate}</code> Ft/EUR árfolyamon konvertál</li>
              <li>• Új cikkszámok automatikusan létrejönnek (<strong>készlet 0, beszerzés 2–3 nap</strong>)</li>
              <li>• Akár ~1M soros XLSX is támogatott — chunked feltöltéssel, háttér-feldolgozással</li>
              <li>• Duplikált cikkszámsorokat automatikusan kezeli (upsert — utolsó nyer)</li>
            </ul>
            <div className="mt-4 pt-3 border-t" style={{ borderColor: "var(--border)" }}>
              <div className="text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: "var(--text-mute)" }}>
                Régi (12%) BMW árak átszámítása
              </div>
              <div className="text-xs mb-2" style={{ color: "var(--text-mute)" }}>
                Egyszeri művelet: a régi 12%-os árréssel feltöltött BMW árakat átszorozza ×1,15/1,12 ≈ <strong>+2,68%</strong>-ra, hogy az új 15%-os formulára álljanak. Csak egyszer fut le; a backend egy markert állít be, hogy ne fusson le újra.
              </div>
              <button
                onClick={runBmwRebase}
                disabled={bmwRebaseRunning}
                className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded inline-flex items-center gap-1.5 transition-colors disabled:opacity-60"
                style={{ background: "#0066B1", color: "#fff" }}
                data-testid="bmw-rebase-btn"
                title="A meglévő BMW árakat átszorozza 1,15/1,12-re a régi 12%-os képletről az új 15%-osra"
              >
                {bmwRebaseRunning ? (
                  <><Loader2 size={12} className="animate-spin" /> Átszámítás...</>
                ) : (
                  <>BMW árak átszámítása (×1,15/1,12)</>
                )}
              </button>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <label
              className="block cursor-pointer text-center p-4 transition-all relative bg-white flex-1 flex flex-col items-center justify-center"
              style={{
                border: `2px dashed ${uploadingBmw ? "var(--brand)" : "#0066B1"}`,
                borderRadius: 4,
                background: uploadingBmw ? "#fff" : "rgba(0,102,177,0.05)",
              }}
              data-testid="bmw-upload-dropzone"
            >
              {uploadingBmw ? (
                <Loader2 className="mx-auto animate-spin mb-2" size={28} style={{ color: "#0066B1" }} />
              ) : (
                <Database className="mx-auto mb-2" size={28} style={{ color: "#0066B1" }} />
              )}
              <div className="font-bold text-sm uppercase" style={{ color: "#0066B1" }}>
                {uploadingBmw ? "Feldolgozás..." : "BMW XLSX feltöltés"}
              </div>
              <div className="text-[11px] mt-1" style={{ color: "var(--text-mute)" }}>.xlsx / .xlsm</div>
              <input
                type="file"
                accept=".xlsx,.xlsm"
                className="hidden"
                onChange={handleBmwFile}
                disabled={uploadingBmw}
                data-testid="bmw-upload-input"
              />
            </label>
          </div>
        </div>

        {lastBmwResult && !uploadingBmw && (
          <div className="px-5 pb-5 grid grid-cols-3 gap-3 text-sm" data-testid="bmw-result">
            <ResultStat label="Sor feldolgozva" value={lastBmwResult.rows} color="#0066B1" />
            <ResultStat label="Új cikkszám" value={lastBmwResult.created} color="var(--in-stock)" />
            <ResultStat label="Frissítve" value={lastBmwResult.updated} color="#0066B1" />
            {lastBmwResult.skipped_no_price > 0 && (
              <div className="col-span-3 text-[11px] p-2 rounded" style={{ background: "#fff5f5", border: "1px solid var(--out-stock)", color: "var(--out-stock)" }}>
                ⚠ {lastBmwResult.skipped_no_price.toLocaleString("hu-HU")} sort kihagytunk hiányzó vagy 0 ár miatt.
              </div>
            )}
          </div>
        )}
        {uploadingBmw && bmwProgress && (
          <div className="px-5 pb-5" data-testid="bmw-progress">
            <div className="flex items-center justify-between text-xs font-bold uppercase mb-2" style={{ color: "#0066B1" }}>
              <span>
                {bmwProgress.phase === "compress" && "Tömörítés a böngészőben..."}
                {bmwProgress.phase === "upload" && "Feltöltés folyamatban (chunked)..."}
                {bmwProgress.phase === "finalize" && "Fájl összeállítása a szerveren..."}
                {(bmwProgress.phase === "process" || !bmwProgress.phase) && "Háttér-feldolgozás folyamatban — ne zárd be az oldalt"}
              </span>
              <span>
                {bmwProgress.phase === "process" || !bmwProgress.phase
                  ? (bmwProgress.total > 0
                      ? `${Math.round((bmwProgress.processed / Math.max(bmwProgress.total, 1)) * 100)}%`
                      : "Fájl olvasása...")
                  : `${bmwProgress.upload_percent || 0}%`}
              </span>
            </div>
            <div className="w-full h-3 rounded overflow-hidden" style={{ background: "rgba(0,102,177,0.12)" }}>
              <div
                className="h-full transition-all duration-300"
                style={{
                  width:
                    bmwProgress.phase === "process" || !bmwProgress.phase
                      ? `${bmwProgress.total > 0 ? Math.min(100, (bmwProgress.processed / bmwProgress.total) * 100) : 5}%`
                      : `${Math.max(2, bmwProgress.upload_percent || 0)}%`,
                  background: "linear-gradient(90deg, #0066B1 0%, #4A8FCB 100%)",
                }}
                data-testid="bmw-progress-bar"
              />
            </div>
            <div className="mt-2 grid grid-cols-3 gap-3 text-xs" style={{ color: "var(--text-mute)" }}>
              <span><strong>{bmwProgress.processed.toLocaleString("hu-HU")}</strong>{bmwProgress.total > 0 && ` / ${bmwProgress.total.toLocaleString("hu-HU")}`} sor</span>
              <span>Új: <strong style={{ color: "var(--in-stock)" }}>{bmwProgress.created.toLocaleString("hu-HU")}</strong></span>
              <span>Frissítve: <strong style={{ color: "#0066B1" }}>{bmwProgress.updated.toLocaleString("hu-HU")}</strong></span>
            </div>
          </div>
        )}
      </div>

      {/* CSV import — KÉSZLET (ETS) */}
      <div className="card-elev mb-5 overflow-hidden">
        <div className="section-title flex items-center gap-2">
          <Database size={14} /> Készlet feltöltés (ETS CSV / TSV) — csak készletet módosít
        </div>
        <div className="p-5 grid lg:grid-cols-[1fr_320px] gap-5 items-stretch">
          <div className="text-sm" style={{ color: "var(--text)" }}>
            <ul className="space-y-1.5">
              <li>• <strong>Csak a készletet (stock) frissíti</strong> — az árakat NEM módosítja</li>
              <li>• <strong>Mintaformátum: ETS_55 export (VAG ETS rendszerből)</strong></li>
              <li>• Fejléc: <code className="font-mono">ET_NR;BEZEICHNUNG;AKT_BESTAND</code> (vagy összevont fejléc, mind kezelt)</li>
              <li>• Sorok: <code className="font-mono">cikkszám;megnevezés;készlet</code> — pontosvesszővel</li>
              <li>• Cikkszámban a szóköz megengedett (pl. <code className="font-mono">000087010R YPN</code>)</li>
              <li>• Az itt feltöltött cikkszámok <strong style={{ color: "var(--in-stock)" }}>Raktáron · azonnal szállítható</strong> jelölést kapnak</li>
              <li>• 20 000+ sorra optimalizálva (a tesztben 22 361 sor / 0 hiba)</li>
            </ul>
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-xs font-bold uppercase">
              Márka (kötelező többfájlos feltöltéshez)
            </label>
            <select
              value={stockBrand}
              onChange={(e) => setStockBrand(e.target.value)}
              className="input !py-1.5"
              data-testid="stock-brand-select"
            >
              <option value="">— Cikkszám előtag alapján —</option>
              <option value="audi">Audi</option>
              <option value="vw">Volkswagen</option>
              <option value="skoda">Škoda</option>
              <option value="seat">Seat</option>
              <option value="bmw">BMW</option>
              <option value="mercedes">Mercedes</option>
            </select>
            <label
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className="block cursor-pointer text-center p-6 transition-all relative bg-white flex-1 flex flex-col items-center justify-center"
              style={{
                border: `2px dashed ${dragOver ? "var(--signal)" : uploading ? "var(--brand)" : "var(--border-hi)"}`,
                borderRadius: 4,
              }}
              data-testid="csv-upload-dropzone"
            >
              {uploading ? (
                <Loader2 className="mx-auto animate-spin mb-2" size={28} style={{ color: "var(--brand)" }} />
              ) : (
                <Upload className="mx-auto mb-2" size={28} style={{ color: dragOver ? "var(--signal)" : "var(--text-mute)" }} />
              )}
              <div className="font-bold text-sm uppercase">
                {uploading ? "Importálás..." : dragOver ? "Engedje el" : "Húzza ide a készlet CSV-t"}
              </div>
              <div className="text-[11px] mt-1" style={{ color: "var(--text-mute)" }}>vagy kattintson</div>
              <input ref={fileRef} type="file" accept=".csv,.tsv,.txt" className="hidden" onChange={handleFile} disabled={uploading} data-testid="csv-upload-input" />
            </label>
          </div>
        </div>

        {lastResult && (
          <div className="px-5 pb-5 grid grid-cols-3 gap-3 text-sm" data-testid="csv-result">
            <ResultStat label="Beszúrva" value={lastResult.inserted} color="var(--in-stock)" />
            <ResultStat label="Frissítve" value={lastResult.updated} color="var(--brand)" />
            <ResultStat label="Hibák" value={lastResult.error_count ?? lastResult.errors?.length ?? 0} color={(lastResult.error_count || lastResult.errors?.length) ? "var(--out-stock)" : "var(--text-mute)"} />
            {(lastResult.layout || lastResult.delimiter) && (
              <div className="col-span-3 text-[11px]" style={{ color: "var(--text-mute)" }}>
                Felismert: formátum=<strong style={{ color: "var(--brand)" }}>{lastResult.layout || "header"}</strong>, elválasztó=<strong style={{ color: "var(--brand)" }}>{JSON.stringify(lastResult.delimiter || ",")}</strong>
              </div>
            )}
          </div>
        )}
      </div>

      {showCreate && <CreateProductForm onDone={() => { setShowCreate(false); fetchData(); }} onClose={() => setShowCreate(false)} />}
      {editing && (
        <CreateProductForm
          initial={editing}
          onDone={() => { setEditing(null); fetchData(); }}
          onClose={() => setEditing(null)}
        />
      )}

      {/* PROMO upload — Akciós árlista (AVP / Verkaufsaktion) */}
      <div className="card-elev mb-5 overflow-hidden" data-testid="promo-upload-card">
        <div className="section-title flex items-center gap-2" style={{ background: "linear-gradient(180deg, #c8102e 0%, #8b0000 100%)" }}>
          <Flame size={14} /> Akciós árlista feltöltés (AVP / Verkaufsaktion) — piros AKCIÓ jelöléssel
        </div>
        <div className="p-5 grid lg:grid-cols-[1fr_320px] gap-5 items-stretch">
          <div className="text-sm" style={{ color: "var(--text)" }}>
            <ul className="space-y-1.5">
              <li>• <strong>Akciós ár (Ihr Preis)</strong> és <strong>listaár (UPE)</strong> egyszerre — piros megjelöléssel</li>
              <li>• <strong>Formátum:</strong> XLSX/CSV — fejléc: <code className="font-mono">Teilenummer · Bezeichnung · UPE · RG · Rabatt % · Ihr Preis · Menge</code></li>
              <li>• A fejléc fölötti 1-2 sor a <strong>kampány neve</strong> (pl. „AVP Verkaufsaktion 2026 / Original Starterbatterien")</li>
              <li>• Az <strong>új cikkszámok automatikusan létrejönnek</strong> (beszerzés 2–3 nap, készlet=0)</li>
              <li>• A vevő márka-árrése az akciós árra is rárakódik (így marad a haszon)</li>
              <li>• <strong>Lejárati dátum</strong> opcionális — manuálisan is le lehet zárni az alábbi gombbal</li>
              <li>• <strong>Az akció NEM írja felül az eredeti cikkszámot</strong> — külön sorként jelenik meg a katalógusban (eredeti Audi/VW ár mellett az akciós ár)</li>
            </ul>
            <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--border)" }}>
              <div className="text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: "var(--text-mute)" }}>
                Régi akciók szétválasztása (migráció)
              </div>
              <div className="text-xs mb-2" style={{ color: "var(--text-mute)" }}>
                Egyszeri művelet: a régi „felülíró" akciókat szétválasztja külön <code className="font-mono">brand=&quot;promo&quot;</code> sorokra, és helyreállítja az eredeti termékek árát. Csak az első deploy után fusson le egyszer.
              </div>
              <button
                onClick={runSplitPromoOverlays}
                disabled={splitPromoRunning}
                className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded inline-flex items-center gap-1.5 transition-colors disabled:opacity-60"
                style={{ background: "#c8102e", color: "#fff" }}
                data-testid="split-promo-btn"
              >
                {splitPromoRunning ? (
                  <><Loader2 size={12} className="animate-spin" /> Szétválasztás...</>
                ) : (
                  <>Régi akciók szétválasztása</>
                )}
              </button>
            </div>
            <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--border)" }}>
              <div className="text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: "var(--text-mute)" }}>
                Akciós cikkek márkájának pótlása (a vevői árrés miatt)
              </div>
              <div className="text-xs mb-2" style={{ color: "var(--text-mute)" }}>
                A cikkszám-előtagból nem mindig olvasható ki a márka (pl. <code className="font-mono">04L*</code>, <code className="font-mono">03H*</code> motor-cikkszámok).
                Ez a gomb minden ilyen akciós cikknél megkeresi az ugyanazon SKU-val rendelkező normál (Audi/VW/Škoda/SEAT/BMW/Mercedes) változatot és átveszi a márkáját.
                Ezután <strong>a vevő márka-árrése helyesen rárakódik az akciós árra is</strong>.
              </div>
              <button
                onClick={runPromoBackfill}
                disabled={promoBackfillRunning}
                className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded inline-flex items-center gap-1.5 transition-colors disabled:opacity-60"
                style={{ background: "#c8102e", color: "#fff" }}
                data-testid="promo-backfill-btn"
              >
                {promoBackfillRunning ? (
                  <><Loader2 size={12} className="animate-spin" /> Pótlás...</>
                ) : (
                  <>Akciós márkák pótlása</>
                )}
              </button>
            </div>
            {promoStatus && promoStatus.total > 0 && (
              <div className="mt-3 p-3 rounded" style={{ background: "#fff5f5", border: "1px solid #c8102e" }} data-testid="active-promos">
                <div className="text-xs font-bold uppercase mb-2" style={{ color: "#c8102e" }}>
                  <Flame size={12} className="inline mb-0.5" /> Aktív akciók ({promoStatus.total} termék)
                </div>
                <div className="space-y-1.5">
                  {promoStatus.campaigns.map((c, i) => {
                    const isEditing = editingPromoLabel === c.label;
                    return (
                      <div key={i} className="flex items-center justify-between text-xs gap-2">
                        <div className="flex-1 min-w-0">
                          {isEditing ? (
                            <div className="flex items-center gap-1.5">
                              <input
                                autoFocus
                                value={editingPromoLabelText}
                                onChange={(e) => setEditingPromoLabelText(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") renamePromoGroup(c.label);
                                  if (e.key === "Escape") { setEditingPromoLabel(null); setEditingPromoLabelText(""); }
                                }}
                                className="input !py-1 text-xs flex-1"
                                placeholder="Új akciónév"
                                data-testid={`promo-rename-input-${i}`}
                              />
                              <button
                                onClick={() => renamePromoGroup(c.label)}
                                className="btn-primary !py-1 !px-2 text-[10px]"
                                data-testid={`promo-rename-save-${i}`}
                              >
                                Ment
                              </button>
                              <button
                                onClick={() => { setEditingPromoLabel(null); setEditingPromoLabelText(""); }}
                                className="btn-ghost !py-1 !px-2 text-[10px]"
                                data-testid={`promo-rename-cancel-${i}`}
                              >
                                Mégse
                              </button>
                            </div>
                          ) : (
                            <>
                              <div className="font-bold truncate" title={c.label}>{c.label}</div>
                              <div style={{ color: "var(--text-mute)" }}>
                                {c.count} termék
                                {c.expires_at && <> · Lejár: {new Date(c.expires_at).toLocaleDateString("hu-HU")}</>}
                              </div>
                            </>
                          )}
                        </div>
                        {!isEditing && (
                          <>
                            <button
                              onClick={() => {
                                setEditingPromoLabel(c.label);
                                setEditingPromoLabelText(c.label);
                              }}
                              className="btn-ghost !py-1 !px-2 text-[10px]"
                              data-testid={`promo-rename-${i}`}
                              title="Akciónév szerkesztése"
                            >
                              <Edit2 size={11} />
                            </button>
                            <button
                              onClick={() => clearPromo(c.label)}
                              className="btn-danger !py-1 !px-2 text-[10px]"
                              data-testid={`clear-promo-${i}`}
                            >
                              Lezárás
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
                <button
                  onClick={() => clearPromo(null)}
                  className="btn-ghost !py-1 !px-2 text-[10px] mt-2"
                  data-testid="clear-all-promos"
                >
                  Minden akció lezárása
                </button>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-xs font-bold uppercase flex items-center gap-1.5">
              <Calendar size={12} /> Lejárati dátum (opcionális)
            </label>
            <input
              type="date"
              className="input !py-1.5"
              value={promoExpiry}
              onChange={(e) => setPromoExpiry(e.target.value)}
              data-testid="promo-expiry-input"
            />
            <label
              className="block cursor-pointer text-center p-4 transition-all relative bg-white flex-1 flex flex-col items-center justify-center"
              style={{
                border: `2px dashed ${uploadingPromo ? "var(--brand)" : "#c8102e"}`,
                borderRadius: 4,
                background: uploadingPromo ? "#fff" : "rgba(200,16,46,0.05)",
              }}
              data-testid="promo-upload-dropzone"
            >
              {uploadingPromo ? (
                <Loader2 className="mx-auto animate-spin mb-2" size={28} style={{ color: "#c8102e" }} />
              ) : (
                <Flame className="mx-auto mb-2" size={28} style={{ color: "#c8102e" }} />
              )}
              <div className="font-bold text-sm uppercase" style={{ color: "#c8102e" }}>
                {uploadingPromo ? "Feldolgozás..." : "Húzza ide az akciós fájlt"}
              </div>
              <div className="text-[11px] mt-1" style={{ color: "var(--text-mute)" }}>XLSX / CSV</div>
              <input
                type="file"
                accept=".csv,.tsv,.txt,.xlsx,.xlsm"
                className="hidden"
                onChange={handlePromoFile}
                disabled={uploadingPromo}
                data-testid="promo-upload-input"
              />
            </label>
          </div>
        </div>

        {lastPromoResult && (
          <div className="px-5 pb-5 grid grid-cols-3 gap-3 text-sm" data-testid="promo-result">
            <ResultStat label="Sor feldolgozva" value={lastPromoResult.rows} color="var(--brand)" />
            <ResultStat label="Frissítve" value={lastPromoResult.updated} color="var(--in-stock)" />
            <ResultStat label="Új cikkszám" value={lastPromoResult.created} color="#c8102e" />
            <div className="col-span-3 text-[11px]" style={{ color: "var(--text-mute)" }}>
              ✓ Kampány: <strong style={{ color: "#c8102e" }}>{lastPromoResult.label}</strong>
              {lastPromoResult.expires_at && (
                <> · Lejár: <strong>{new Date(lastPromoResult.expires_at).toLocaleDateString("hu-HU")}</strong></>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="card-flat p-3 mb-3 flex items-center gap-2 flex-wrap">
        <Search size={14} style={{ color: "var(--text-mute)" }} />
        <input
          className="input !py-1.5 flex-1 min-w-[200px]"
          placeholder="Cikkszám vagy megnevezés keresése..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="admin-products-search"
        />
        {q && <button onClick={() => setQ("")} style={{ color: "var(--text-mute)" }}><X size={14} /></button>}
        <select
          className="input !py-1.5 !w-auto text-xs"
          value={brand}
          onChange={(e) => setBrand(e.target.value)}
          data-testid="admin-products-brand-filter"
        >
          <option value="">— Minden márka —</option>
          <option value="audi">Audi</option>
          <option value="vw">VW</option>
          <option value="skoda">Škoda</option>
          <option value="seat">SEAT</option>
          <option value="bmw">BMW</option>
          <option value="mercedes">Mercedes-Benz</option>
        </select>
      </div>

      <div className="card-elev overflow-hidden">
        <div className="section-title">Alkatrész lista</div>
        <div className="overflow-x-auto">
          <table className="table-b2b" data-testid="admin-products-table">
            <thead>
              <tr>
                <th>Cikkszám</th>
                <th>Márka</th>
                <th>Megnevezés</th>
                <th style={{ width: 80 }}>Készlet</th>
                <th style={{ width: 110 }} className="text-right">Alapár</th>
                <th style={{ width: 100 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-8" style={{ color: "var(--text-mute)" }}>Betöltés...</td></tr>
              ) : data.items.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-8" style={{ color: "var(--text-mute)" }}>Nincs alkatrész. Töltsön fel CSV-t fent.</td></tr>
              ) : (
                data.items.map((p) => {
                  const brand = p.brand && BRANDS[p.brand];
                  return (
                    <tr key={p.id} data-testid={`product-row-${p.sku}`}>
                      <td>
                        <span className="font-mono text-xs font-bold" style={{ color: "var(--brand)" }}>{p.sku}</span>
                        {p.on_promo && (
                          <span
                            className="ml-1.5 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase px-1 py-0.5 rounded"
                            style={{ background: "#c8102e", color: "#fff" }}
                            title={p.promo_label || "Akció"}
                            data-testid={`promo-badge-${p.sku}`}
                          >
                            <Flame size={9} /> Akció
                          </span>
                        )}
                        {p.source === "manual" && (
                          <span
                            className="ml-1.5 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase px-1 py-0.5 rounded"
                            style={{ background: "#0f766e", color: "#fff" }}
                            title="Manuálisan felvitt termék"
                            data-testid={`manual-badge-${p.sku}`}
                          >
                            Manuális
                          </span>
                        )}
                      </td>
                      <td>
                        {brand ? (
                          <span className="inline-flex items-center gap-1.5">
                            <img src={darkLogoFor(brand)} alt={brand.label} className="h-3.5 w-3.5 object-contain" />
                            <span className="text-xs uppercase">{brand.label}</span>
                          </span>
                        ) : <span style={{ color: "var(--text-disabled)" }}>—</span>}
                      </td>
                      <td className="font-bold">{p.name}</td>
                      <td className="font-mono">{p.stock}</td>
                      <td className="text-right font-mono">
                        {p.on_promo && p.promo_price ? (
                          <span>
                            <span style={{ color: "#c8102e", fontWeight: 700 }}>{formatMoney(p.promo_price)}</span>
                            <span className="ml-1 line-through text-[10px]" style={{ color: "var(--text-disabled)" }}>{formatMoney(p.base_price)}</span>
                          </span>
                        ) : p.base_price === 0 ? (
                          <span style={{ color: "var(--text-disabled)" }}>—</span>
                        ) : (
                          formatMoney(p.base_price)
                        )}
                      </td>
                      <td>
                        <div className="flex gap-1 justify-end">
                          <button
                            onClick={() => setEditing(p)}
                            className="btn-ghost !py-1 !px-2 text-xs"
                            title="Szerkesztés"
                            data-testid={`edit-product-${p.sku}`}
                          >
                            <Edit2 size={12} />
                          </button>
                          <button onClick={() => deleteProduct(p.id)} className="btn-danger !py-1 !px-2 text-xs" data-testid={`delete-product-${p.sku}`}>
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="card-flat p-3 mt-3 flex items-center justify-between">
          <div className="text-xs" style={{ color: "var(--text-mute)" }}>
            <strong>{page + 1}</strong>. oldal / {totalPages.toLocaleString("hu-HU")}
          </div>
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="btn-ghost !py-1 !px-2 text-[11px] disabled:opacity-30">Előző</button>
            <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="btn-primary !py-1 !px-2 text-[11px] disabled:opacity-30">Következő</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ResultStat({ label, value, color }) {
  return (
    <div className="card-flat p-2 text-center">
      <div className="text-[10px] uppercase font-bold" style={{ color: "var(--text-mute)" }}>{label}</div>
      <div className="text-xl font-bold" style={{ color }}>{value}</div>
    </div>
  );
}

const FIELD_LABELS = {
  sku: "Cikkszám", name: "Megnevezés", category: "Kategória",
  image_url: "Kép URL", stock: "Készlet", base_price: "Alapár (nettó)", description: "Leírás",
  brand: "Márka",
};

// Common brand options for the manual product form (includes existing
// catalogue brands + popular new ones the admin might add manually).
const MANUAL_BRAND_OPTIONS = [
  { value: "", label: "— Nincs / Általános —" },
  { value: "audi", label: "Audi" },
  { value: "vw", label: "Volkswagen" },
  { value: "skoda", label: "Škoda" },
  { value: "seat", label: "SEAT" },
  { value: "bmw", label: "BMW" },
  { value: "mercedes", label: "Mercedes-Benz" },
  { value: "hyundai", label: "Hyundai" },
  { value: "kia", label: "Kia" },
  { value: "toyota", label: "Toyota" },
  { value: "ford", label: "Ford" },
  { value: "opel", label: "Opel" },
  { value: "renault", label: "Renault" },
  { value: "peugeot", label: "Peugeot" },
  { value: "citroen", label: "Citroën" },
  { value: "fiat", label: "Fiat" },
  { value: "nissan", label: "Nissan" },
  { value: "mazda", label: "Mazda" },
  { value: "suzuki", label: "Suzuki" },
  { value: "honda", label: "Honda" },
  { value: "__custom__", label: "Egyéb (saját)..." },
];

function CreateProductForm({ initial, onDone, onClose }) {
  const isEdit = !!initial?.id;
  const initialBrand = (initial?.brand || "").toLowerCase();
  const initialBrandKnown = MANUAL_BRAND_OPTIONS.some(
    (o) => o.value && o.value !== "__custom__" && o.value === initialBrand,
  );
  const [form, setForm] = useState({
    sku: initial?.sku || "",
    name: initial?.name || "",
    description: initial?.description || "",
    category: initial?.category || "",
    brand: initialBrand,
    stock: initial?.stock ?? 0,
    base_price: initial?.base_price ?? 0,
    image_url: initial?.image_url || "",
  });
  const [brandMode, setBrandMode] = useState(
    initialBrand && !initialBrandKnown ? "__custom__" : initialBrand || "",
  );
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        brand: (form.brand || "").trim().toLowerCase(),
        stock: Number(form.stock),
        base_price: Number(form.base_price),
        group_prices: initial?.group_prices || {},
      };
      if (isEdit) {
        await api.put(`/admin/products/${initial.id}`, payload);
        toast.success("Alkatrész frissítve");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Alkatrész létrehozva");
      }
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="card-elev mb-5" data-testid="create-product-form">
      <div className="section-title justify-between">
        <span>{isEdit ? `Alkatrész szerkesztése — ${initial.sku}` : "Új alkatrész létrehozása (manuális)"}</span>
        <button type="button" onClick={onClose} style={{ color: "#fff" }}><X size={16} /></button>
      </div>
      <div className="p-5 grid sm:grid-cols-2 gap-3">
        {["sku", "name", "category", "image_url"].map((k) => (
          <div key={k}>
            <label className="text-xs font-bold uppercase block mb-1">{FIELD_LABELS[k]}</label>
            <input
              className="input"
              required={k === "sku" || k === "name"}
              disabled={isEdit && k === "sku"}
              value={form[k]}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              data-testid={`new-product-${k}`}
            />
          </div>
        ))}
        <div>
          <label className="text-xs font-bold uppercase block mb-1">{FIELD_LABELS.brand}</label>
          <select
            className="input"
            value={brandMode}
            onChange={(e) => {
              const v = e.target.value;
              setBrandMode(v);
              if (v !== "__custom__") {
                setForm({ ...form, brand: v });
              } else {
                setForm({ ...form, brand: "" });
              }
            }}
            data-testid="new-product-brand-select"
          >
            {MANUAL_BRAND_OPTIONS.map((o) => (
              <option key={o.value || "_none"} value={o.value}>{o.label}</option>
            ))}
          </select>
          {brandMode === "__custom__" && (
            <input
              className="input mt-2"
              placeholder="Pl. hyundai, kia, toyota..."
              value={form.brand}
              onChange={(e) => setForm({ ...form, brand: e.target.value })}
              data-testid="new-product-brand-custom"
            />
          )}
          <div className="text-[10px] mt-1" style={{ color: "var(--text-mute)" }}>
            A vevő márka-árrése (markup) automatikusan rárakódik erre a márkára.
          </div>
        </div>
        <div>
          <label className="text-xs font-bold uppercase block mb-1">{FIELD_LABELS.stock}</label>
          <input type="number" className="input" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="new-product-stock" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase block mb-1">{FIELD_LABELS.base_price}</label>
          <input type="number" step="0.01" className="input" required value={form.base_price} onChange={(e) => setForm({ ...form, base_price: e.target.value })} data-testid="new-product-price" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-bold uppercase block mb-1">{FIELD_LABELS.description}</label>
          <textarea className="input" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="new-product-description" />
        </div>
      </div>
      <div className="px-5 pb-5 flex gap-2">
        <button type="submit" disabled={saving} className="btn-primary" data-testid="save-new-product">
          {saving ? "Mentés..." : isEdit ? "Módosítások mentése" : "Alkatrész mentése"}
        </button>
        <button type="button" onClick={onClose} className="btn-ghost">Mégse</button>
      </div>
    </form>
  );
}
